import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import api from '@/services/api'

type ActivityEvent = {
  id: number
  user_email: string | null
  event_type: string
  auth_source: string | null
  route: string | null
  method: string | null
  status_code: number | null
  ip_address: string | null
  browser: string | null
  os: string | null
  device_type: string | null
  location: string | null
  details: string | null
  created_at: string
}

const LOGIN_TYPES = new Set([
  'login_success',
  'login_passkey_success',
  'login_2fa_success',
  'login_failed',
  'login_2fa_failed',
  'login_2fa_challenge',
  'login_2fa_enrolment_required',
  'login_2fa_throttled',
  'login_passkey_required',
])

function formatWhen(value: string) {
  return new Date(value).toLocaleString()
}

function statusLabel(event: ActivityEvent): string {
  if (event.event_type.includes('failed')) return 'Failed'
  if (event.event_type === 'login_2fa_challenge') return 'MFA Challenge'
  if (event.event_type === 'login_2fa_enrolment_required') return 'MFA Enrollment'
  if (event.event_type === 'login_2fa_throttled') return 'Locked'
  if (event.event_type === 'login_passkey_required') return 'Passkey Required'
  if (event.event_type.includes('success')) return 'Success'
  return event.event_type
}

function deviceLabel(event: ActivityEvent): string {
  const parts = [event.browser, event.os].filter(Boolean)
  if (parts.length) return parts.join(' / ')
  return event.device_type ?? 'Unknown'
}

function moduleFromRoute(route: string | null): string {
  if (!route) return 'System'
  const segment = route.split('/').filter(Boolean)[2] ?? route
  return segment.replace(/-/g, ' ')
}

export default function AdminActivitySettingsTab() {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ActivityEvent[]>('/admin/activity', { params: { limit: 300 } })
      setEvents(res.data)
    } catch {
      setError('Unable to load activity audit log.')
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const loginHistory = useMemo(
    () => events.filter((e) => LOGIN_TYPES.has(e.event_type)),
    [events],
  )

  const auditLog = useMemo(
    () => events.filter((e) => !LOGIN_TYPES.has(e.event_type)),
    [events],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-medium text-stone-100">Activity & Audit</h3>
        <button type="button" className="btn-secondary flex items-center gap-1.5 text-[12px] px-3 py-1.5" onClick={() => void load()}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && <div className="rounded border border-red-400/20 bg-red-400/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}

      <div>
        <h4 className="text-[12px] font-medium text-stone-300 mb-2">Login History</h4>
        {loading ? (
          <div className="text-[12px] text-stone-500">Loading…</div>
        ) : loginHistory.length === 0 ? (
          <div className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-6 text-center text-[12px] text-stone-500">
            No login events recorded yet.
          </div>
        ) : (
          <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-hidden max-h-[340px] overflow-y-auto">
            <table className="dense-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>User</th>
                  <th>Timestamp</th>
                  <th>IP Address</th>
                  <th>Location</th>
                  <th>Device / Browser</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loginHistory.map((event) => (
                  <tr key={event.id}>
                    <td className="font-medium text-stone-200">{event.user_email ?? 'Unknown'}</td>
                    <td className="font-mono text-[11px] text-stone-400">{formatWhen(event.created_at)}</td>
                    <td className="font-mono text-[11px]">{event.ip_address ?? '—'}</td>
                    <td>{event.location ?? '—'}</td>
                    <td className="text-[11px] text-stone-400">{deviceLabel(event)}</td>
                    <td className="text-[11px] text-stone-300">{statusLabel(event)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h4 className="text-[12px] font-medium text-stone-300 mb-2">Audit Log</h4>
        {loading ? (
          <div className="text-[12px] text-stone-500">Loading…</div>
        ) : auditLog.length === 0 ? (
          <div className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-6 text-center text-[12px] text-stone-500">
            No API audit events recorded yet.
          </div>
        ) : (
          <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-hidden max-h-[340px] overflow-y-auto">
            <table className="dense-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Module</th>
                  <th>Details</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((event) => (
                  <tr key={event.id}>
                    <td className="font-mono text-[11px] text-stone-400">{formatWhen(event.created_at)}</td>
                    <td className="font-medium text-stone-200">{event.user_email ?? 'Unknown'}</td>
                    <td className="text-amber-300/80">{event.event_type}</td>
                    <td>
                      <span className="rounded border border-amber-400/15 bg-amber-400/[0.06] px-1.5 py-0.5 text-[10px] text-amber-300">
                        {moduleFromRoute(event.route)}
                      </span>
                    </td>
                    <td className="max-w-[260px] truncate text-[11px] text-stone-400" title={event.details ?? ''}>
                      {[event.method, event.route, event.status_code != null ? `HTTP ${event.status_code}` : null].filter(Boolean).join(' ')}
                    </td>
                    <td className="font-mono text-[11px] text-stone-500">{event.ip_address ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
